import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { 
    ArrowLeft, 
    Save, 
    Monitor, 
    MapPin, 
    Building, 
    Hash, 
    User, 
    Globe, 
    Navigation,
    Home,
    Phone,
    Mail
} from 'lucide-react';
import { addVendingMachine } from '../../actions/vendingMachine';
import { getProfiles } from '../../actions/profile';

const AddVendingMachine = ({ addVendingMachine, profiles, getProfiles, loading }) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (profiles.length === 0) {
            getProfiles();
        }
    }, [profiles, getProfiles]);

    const [formData, setFormData] = useState({
        name: '',
        vendingMachineId: '',
        profileName: '',
        profile: '',
        location: {
            type: 'Point',
            coordinates: [0, 0] // [longitude, latitude]
        },
        address: {
            number: '',
            name: '',
            addressLine: '',
            city: '',
            state: '',
            country: 'India',
            pincode: ''
        }
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [locationMethod, setLocationMethod] = useState('manual'); // 'manual' or 'current'
    const [profileSelected, setProfile] = useState('');

    const onChange = e => {
        const { name, value } = e.target;
        
        if (name === 'profile') {
            setProfile(value);
            const selectedProfile = profiles.find(p => p._id === value);
            setFormData({
                ...formData,
                profileName: selectedProfile ? selectedProfile.name : '',
                profile: value
            });
        } else if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            setFormData({
                ...formData,
                address: {
                    ...formData.address,
                    [addressField]: value
                }
            });
        } else if (name === 'latitude' || name === 'longitude') {
            const coordinates = [...formData.location.coordinates];
            if (name === 'latitude') {
                coordinates[1] = parseFloat(value) || 0;
            } else {
                coordinates[0] = parseFloat(value) || 0;
            }
            setFormData({
                ...formData,
                location: {
                    ...formData.location,
                    coordinates
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFormData({
                        ...formData,
                        location: {
                            type: 'Point',
                            coordinates: [position.coords.longitude, position.coords.latitude]
                        }
                    });
                    setLocationMethod('current');
                },
                (error) => {
                    console.error('Error getting location:', error);
                    alert('Unable to get current location. Please enter coordinates manually.');
                }
            );
        } else {
            alert('Geolocation is not supported by this browser.');
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            await addVendingMachine(formData);
            navigate('/inventory/v1.2/');
        } catch (error) {
            console.error('Error adding vending machine:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const showProfiles = () => {
        return profiles.map((profile, index) => (
            <option key={`profile-${index}`} value={profile._id}>
                {profile.name}
            </option>
        ));
    };

return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <Link 
                            className="btn btn-outline-primary d-flex align-items-center" 
                            to="/inventory/v1.2/"
                        >
                            <ArrowLeft size={16} className="me-2" />
                            Back to Dashboard
            </Link>
                        <h2 className="text-primary mb-0 fw-bold d-flex align-items-center">
                            <Monitor size={28} className="me-3" />
                            Add New Vending Machine
                        </h2>
                        <div></div>
                    </div>
                </div>
            </div>

            <form onSubmit={onSubmit}>
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-8 col-xl-6">
                        {/* Basic Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Monitor size={20} className="me-2" />
                                    Machine Information
                                </h5>
        </div> 
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">
                                            <Monitor size={16} className="me-2" />
                                            Machine Name
                                        </label>
                <input
                                            type="text"
                                            name="name"
                    value={formData.name}
                                            onChange={onChange}
                                            className="form-control form-control-lg"
                                            placeholder="Enter machine name"
                    required
                />
            </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">
                                            <Hash size={16} className="me-2" />
                                            Machine ID
                                        </label>
                <input
                                            type="text"
                                            name="vendingMachineId"
                    value={formData.vendingMachineId}
                                            onChange={onChange}
                                            className="form-control form-control-lg"
                                            placeholder="Enter unique machine ID"
                                            required
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-semibold">
                                            <User size={16} className="me-2" />
                                            Profile
                                        </label>
                                        <select
                                            name="profile"
                                            value={profileSelected}
                                            onChange={onChange}
                                            className="form-select form-select-lg"
                                            required
                                        >
                                            <option value="">Select Profile</option>
                                            {showProfiles()}
                                        </select>
                                        <small className="text-muted">
                                            Choose the product profile for this machine
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Location Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-success text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <MapPin size={20} className="me-2" />
                                    Location & Coordinates
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label fw-semibold mb-0">
                                                <Navigation size={16} className="me-2" />
                                                GPS Coordinates
                                            </label>
                                            <button
                                                type="button"
                                                onClick={getCurrentLocation}
                                                className="btn btn-outline-primary btn-sm"
                                            >
                                                <Navigation size={14} className="me-1" />
                                                Use Current Location
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Latitude</label>
                                        <input
                                            type="number"
                                            name="latitude"
                                            value={formData.location.coordinates[1]}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="12.9716"
                                            step="any"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Longitude</label>
                                        <input
                                            type="number"
                                            name="longitude"
                                            value={formData.location.coordinates[0]}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="77.5946"
                                            step="any"
                                            required
                                        />
                                    </div>
                                    {locationMethod === 'current' && (
                                        <div className="col-12">
                                            <div className="alert alert-success d-flex align-items-center">
                                                <Navigation size={16} className="me-2" />
                                                <small>Location updated using GPS coordinates</small>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Address Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-info text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Building size={20} className="me-2" />
                                    Address Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">
                                            <Hash size={16} className="me-2" />
                                            Building/Door Number
                                        </label>
                                        <input
                                            type="text"
                                            name="address.number"
                                            value={formData.address.number}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="#34, Block A"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">
                                            <Building size={16} className="me-2" />
                                            Location Name
                                        </label>
                                        <input
                                            type="text"
                                            name="address.name"
                                            value={formData.address.name}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="Office lobby, Cafeteria, etc."
                                            required
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-semibold">
                                            <Home size={16} className="me-2" />
                                            Address Line
                                        </label>
                                        <input
                                            type="text"
                                            name="address.addressLine"
                                            value={formData.address.addressLine}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="Street name, area, landmark"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">City</label>
                                        <input
                                            type="text"
                                            name="address.city"
                                            value={formData.address.city}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="Bangalore"
                    required
                />
            </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">State</label>
                <input
                                            type="text"
                                            name="address.state"
                                            value={formData.address.state}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="Karnataka"
                    required
                />
            </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">Country</label>
                <select
                                            name="address.country"
                                            value={formData.address.country}
                                            onChange={onChange}
                                            className="form-select"
                    required
                                        >
                                            <option value="India">India</option>
                                            <option value="USA">USA</option>
                                            <option value="UK">UK</option>
                                            <option value="Canada">Canada</option>
                                            {/* Add more countries as needed */}
                </select>
            </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">Pincode</label>
                                        <input
                                            type="text"
                                            name="address.pincode"
                                            value={formData.address.pincode}
                                            onChange={onChange}
                                            className="form-control"
                                            placeholder="560001"
                                            pattern="[0-9]{6}"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="card shadow-sm">
                            <div className="card-body text-center">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="btn btn-primary btn-lg px-5 py-3 fw-bold"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Creating Machine...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={20} className="me-2" />
                                            Create Vending Machine
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Summary Sidebar */}
                    <div className="col-12 col-lg-4 col-xl-3 mt-4 mt-lg-0">
                        <div className="card shadow-sm position-sticky" style={{ top: '20px' }}>
                            <div className="card-header bg-secondary text-white">
                                <h6 className="mb-0">
                                    <Globe size={16} className="me-2" />
                                    Summary
                                </h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <small className="text-muted d-block">Machine Name</small>
                                    <div className="fw-semibold">
                                        {formData.name || 'Not specified'}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Machine ID</small>
                                    <div className="fw-semibold">
                                        {formData.vendingMachineId || 'Not specified'}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Profile</small>
                                    <div className="fw-semibold">
                                        {formData.profileName || 'Not selected'}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Location</small>
                                    <div className="small">
                                        <strong>Lat:</strong> {formData.location.coordinates[1] || 0}<br />
                                        <strong>Lng:</strong> {formData.location.coordinates[0] || 0}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Address</small>
                                    <div className="small">
                                        {formData.address.number && `${formData.address.number}, `}
                                        {formData.address.name && `${formData.address.name}, `}
                                        {formData.address.city && `${formData.address.city}, `}
                                        {formData.address.state && `${formData.address.state}, `}
                                        {formData.address.pincode}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
      </form>
    </div>
  );
};

AddVendingMachine.propTypes = {
    addVendingMachine: PropTypes.func.isRequired,
    getProfiles: PropTypes.func.isRequired,
    profiles: PropTypes.array,
    loading: PropTypes.bool
};

const mapStateToProps = state => ({
    profiles: state.profile.profiles,
    loading: state.profile.loading
});

export default connect(mapStateToProps, { addVendingMachine, getProfiles })(AddVendingMachine);
